import type { ReactNode } from 'react';
import type { Pareja } from '../services/api';
import { nombreDeRonda, type CruceResuelto, type CuadroResuelto, type Lado } from '../nucleo/cuadro';
import { ganador } from '../nucleo/marcador';
import { nombrePareja } from './Grupos';
import { Tarjeta, Titulo } from './ui';

// La llave. La misma pieza para el público y para el organizador (que le suma,
// por partido, el botón para cargar el resultado). En el celular se desliza de
// costado, una ronda por vez (como en la maqueta).

/** "1º Grupo A", "Ganador cuartos 2": lo que todavía no se sabe dice de dónde sale. */
export function textoDeLado(
  lado: Lado,
  parejas: Map<number, Pareja>,
  letras: Map<number, string>,
  partidosPorRonda: number[],
): { texto: string; seSabe: boolean } {
  if ('pareja' in lado) return { texto: nombrePareja(parejas.get(lado.pareja)), seSabe: true };
  if ('libre' in lado) return { texto: '—', seSabe: false };
  const e = lado.espera;
  if (e.tipo === 'grupo') return { texto: `${e.puesto}º Grupo ${letras.get(e.grupo) ?? '?'}`, seSabe: false };
  const ronda = nombreDeRonda(partidosPorRonda[e.ronda - 1] ?? 1, true).toLowerCase();
  return { texto: ronda === 'final' ? 'Ganador de la final' : `Ganador ${ronda} ${e.pos + 1}`, seSabe: false };
}

export function VistaCuadro({
  cuadro,
  parejas,
  letras,
  extraPartido,
  paraOrganizador,
  cuando,
}: {
  cuadro: CuadroResuelto;
  parejas: Map<number, Pareja>;
  /** id de grupo -> letra, para escribir "1º Grupo A". */
  letras: Map<number, string>;
  extraPartido?: (cruce: CruceResuelto) => ReactNode;
  /** Muestra los avisos que al público no le sirven (un partido que quedó desfasado). */
  paraOrganizador?: boolean;
  /** id de partido -> "Sáb 18 · 18:00 · Cancha 2" y si está en juego. */
  cuando?: Map<number, { texto: string; enJuego: boolean }>;
}) {
  const porRonda = cuadro.rondas.map((r) => r.length);
  const haySinSaber = cuadro.rondas.flat().some((c) => 'espera' in c.a || 'espera' in c.b);

  return (
    <Tarjeta>
      <Titulo extra={cuadro.rondas.length > 1 ? 'deslizá para ver las rondas →' : undefined}>Cuadro</Titulo>

      {cuadro.campeon !== null && (
        <p className="mb-3 rounded-xl border-2 border-(--color-oro) px-3 py-2">
          <span className="block text-[11px] font-bold uppercase tracking-widest text-(--color-oro)">Campeones</span>
          <span className="font-titulo text-2xl uppercase">{nombrePareja(parejas.get(cuadro.campeon))}</span>
        </p>
      )}

      <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2">
        {cuadro.rondas.map((ronda, i) => (
          <div key={i} className={`flex shrink-0 snap-start flex-col ${cuadro.rondas.length > 1 ? 'w-[78%] max-w-72' : 'w-full'}`}>
            <h3 className="mb-1.5 font-titulo text-xl uppercase text-(--color-apagado)">{nombreDeRonda(ronda.length)}</h3>
            <div className="flex flex-1 flex-col justify-around gap-2.5">
              {ronda.map((c) => (
                <Cruce key={`${c.ronda}-${c.pos}`} cruce={c} parejas={parejas} letras={letras} porRonda={porRonda} esFinal={ronda.length === 1} paraOrganizador={paraOrganizador} cuando={cuando?.get(c.id)}>
                  {extraPartido?.(c)}
                </Cruce>
              ))}
            </div>
          </div>
        ))}
      </div>

      {haySinSaber && (
        <p className="mt-2 text-xs text-(--color-apagado)">
          Lo que está en <i>cursiva</i> todavía no se sabe: se completa solo cuando termina el grupo o el partido.
        </p>
      )}
    </Tarjeta>
  );
}

function Cruce({
  cruce,
  parejas,
  letras,
  porRonda,
  esFinal,
  paraOrganizador,
  cuando,
  children,
}: {
  cruce: CruceResuelto;
  parejas: Map<number, Pareja>;
  letras: Map<number, string>;
  porRonda: number[];
  esFinal: boolean;
  paraOrganizador?: boolean;
  cuando?: { texto: string; enJuego: boolean };
  children?: ReactNode;
}) {
  const m = cruce.marcador;
  const gana = m ? ganador(m) : null;

  const renglon = (lado: 'a' | 'b') => {
    const { texto, seSabe } = textoDeLado(cruce[lado], parejas, letras, porRonda);
    const clase = gana ? (gana === lado ? 'font-bold' : 'text-(--color-apagado)') : '';
    let numeros: ReactNode = null;
    if (m?.wo) numeros = gana === lado ? <span className="text-xs">W.O.</span> : null;
    else if (m?.sets) {
      numeros = [...m.sets, ...(m.tb ? [m.tb] : [])].map(([x, y], i) => {
        const mio = lado === 'a' ? x : y;
        const suyo = lado === 'a' ? y : x;
        return <span key={i} className={`w-5 text-center ${mio > suyo ? 'font-bold' : ''}`}>{mio}</span>;
      });
    }
    return (
      <div className={`flex justify-between gap-2 py-1 text-sm ${clase}`}>
        <span className={seSabe ? '' : 'italic text-(--color-apagado)'}>{texto}</span>
        <span className="flex shrink-0 tabular-nums">{numeros}</span>
      </div>
    );
  };

  return (
    <div
      className={`rounded-xl border px-2.5 py-1.5 bg-(--color-panel)/60 ${
        cuando?.enJuego ? 'border-(--color-vivo)' : esFinal ? 'border-(--color-oro)' : 'border-(--color-borde)'
      }`}
    >
      {renglon('a')}
      <div className="border-t border-(--color-borde)" />
      {renglon('b')}
      {cruce.pasaDirecto && <p className="text-[11px] text-(--color-apagado)">Pasa directo a la ronda siguiente</p>}
      {cuando && (cuando.enJuego || cuando.texto) && (
        <p className="text-[11px] text-(--color-apagado)">
          {cuando.enJuego && <span className="font-bold text-(--color-vivo)">● En juego{cuando.texto ? ' · ' : ''}</span>}
          {cuando.texto}
        </p>
      )}
      {cruce.porAbandono && <p className="text-[11px] text-(--color-apagado)">W.O.: la otra pareja abandonó</p>}
      {paraOrganizador && cruce.conflicto && (
        <p className="mt-1 rounded-lg border border-(--color-alerta) px-2 py-1 text-[11px] text-(--color-alerta)">
          Este partido ya se jugó, pero por un cambio anterior el cuadro ahora pondría a otras parejas acá. Si no corresponde, anulá
          el resultado.
        </p>
      )}
      {children}
    </div>
  );
}
